export type Lang = "en" | "ru";

export const getLang = (): Lang => {
  const stored = localStorage.getItem("lang");
  if (stored === "en" || stored === "ru") return stored;

  const browserLang = navigator.language.toLowerCase();

  if (browserLang.startsWith("ru")) return "ru";
  return "en";
};

export const setLang = (lang: Lang) => {
  localStorage.setItem("lang", lang);
};

export const i18n = {
  login: {
    en: {
      inviteCodeLabel: "Invite Code",
      emailPlaceholder: "Enter your email",
      magicLoginButton: "Login via email",
      magicLinkSent: "Check your email!",
      googleLoginFailed: "Google login failed",
      emailRequired: "Please enter your email",
    },
    ru: {
      inviteCodeLabel: "Код приглашения",
      emailPlaceholder: "Введите ваш email",
      magicLoginButton: "Войти по email",
      magicLinkSent: "Проверьте почту!",
      googleLoginFailed: "Ошибка входа через Google",
      emailRequired: "Введите email",
    },
  },
  profile: {
    en: {
      deckValue: "Deck Value",
      myDeck: "My Deck",
      valueLabel: "Value",
      remainingArtworks: "Remaining Favorited Artworks",

      voteToEarn: "Vote to earn 🌕",
      voteDescription:
        "You get a reward for voting in Battles, depending on your ExpertRate (winners selected / battles).",

      expertRate: "ExpertRate",
      calculating: "Calculating...",
      battleVoteReward: "Battle vote reward",

      voteNow: "Vote now",

      votedInfoPrefix: "You've already voted",
      votedInfoSuffix: "times! Perfect result! Keep going.",
      votedExtra:
        "When there are no ongoing battles you can still vote for a tiny reward and a great pleasure.",

      logout: "Log Out",
      deleteProfile: "Delete Profile",
      cancelDelete: "Cancel Delete Request",

      deleteModalTitle: "Delete User Request",
      deleteModalQuestion:
        "All your data including artworks and wallet will be removed. Are you sure?",
      deleteModalButton: "Delete Me",
    },

    ru: {
      deckValue: "Ценность колоды",
      myDeck: "Моя колода",
      valueLabel: "Ценность",
      remainingArtworks: "Оставшиеся избранные работы",

      voteToEarn: "Голосуй и зарабатывай 🌕",
      voteDescription:
        "Вы получаете награду за голосование в битвах в зависимости от вашего ExpertRate (победы / битвы).",

      expertRate: "ExpertRate",
      calculating: "Рассчитывается...",
      battleVoteReward: "Награда за голос",

      voteNow: "Голосовать",

      votedInfoPrefix: "Вы уже проголосовали",
      votedInfoSuffix: "раз! Отличный результат, продолжайте!",
      votedExtra:
        "Когда нет активных битв, вы всё равно можете голосовать ради небольшого бонуса и удовольствия.",

      logout: "Выйти",
      deleteProfile: "Удалить профиль",
      cancelDelete: "Отменить удаление",

      deleteModalTitle: "Запрос на удаление профиля",
      deleteModalQuestion:
        "Все ваши данные, включая работы и кошелёк, будут удалены. Вы уверены?",
      deleteModalButton: "Удалить аккаунт",
    },
  },
  votesHeader: {
    en: {
      title: "Votes",
      expertRateLabel: "ExpertRate",
    },
    ru: {
      title: "Голоса",
      expertRateLabel: "ExpertRate",
    },
  },
  votes: {
    en: {
      loading: "Getting your votes. Please wait for a second...",
      tabOngoing: "Ongoing",
      tabFinished: "Finished",
      emptyOngoing: "You don't have ongoing votes",
      emptyFinished: "You don't have finished votes",
      voteNow: "Vote now",
    },
    ru: {
      loading: "Загружаем ваши голоса. Пожалуйста, подождите...",
      tabOngoing: "Текущие",
      tabFinished: "Завершённые",
      emptyOngoing: "У вас нет текущих голосований",
      emptyFinished: "У вас нет завершённых голосований",
      voteNow: "Голосовать",
    },
  },
  votePair: {
    en: {
      expertRateShort: "ER",
      expertRateArrow: "→",
      reward: "Reward",
    },
    ru: {
      expertRateShort: "ЕР",
      expertRateArrow: "→",
      reward: "Награда",
    },
  },
  voteEmpty: {
    en: {
      title: "No Battles to vote",
      messageLine1: "We're temporarily out of vote pairs.",
      messageLine2: "Please come back later.",
    },
    ru: {
      title: "Нет битв для голосования",
      messageLine1: "Временно нет доступных пар для голосования.",
      messageLine2: "Пожалуйста, зайдите позже.",
    },
  },
  voteCaptcha: {
    en: {
      title: "Select {select} area",
      balance: "Balance",
      voteButton: "Vote",
    },
    ru: {
      title: "Выберите {select} область",
      balance: "Баланс",
      voteButton: "Голосовать",
    },
  },
  voteBlocked: {
    en: {
      title: "Temporary Blocked",
      message:
        "Looks like you're a bit tired.\nPlease take a rest and come back in",
    },
    ru: {
      title: "Временно заблокировано",
      message: "Похоже, вы немного устали.\nОтдохните и возвращайтесь через",
    },
  },
  vote: {
    en: {
      loadingText: "Getting artworks for a vote. Wait a second, please...",
    },
    ru: {
      loadingText: "Получаем работы для голосования. Подождите секунду...",
    },
  },
  silentVote: {
    en: {
      tip: {
        step1Title: "The question",
        step1Text: "Try to choose the art that best fits the question.",
        step2Title: "Time Limit",
        step2Text: "You have only 60 seconds to make a choice.",
        step3Title: "The Deck",
        step3Text: "Hit ⭐ to add the artwork to your deck.",
        step3Btn: "OK, got it",
        nextBtn: "Next",
      },
      selectArtwork: "Select the best artwork",
      balance: "Balance",
      voteBtn: {
        default: "Vote",
        withDeck: "Vote & Add to Deck",
      },
    },
    ru: {
      tip: {
        step1Title: "Вопрос",
        step1Text: "Выберите арт, который лучше всего подходит к вопросу.",
        step2Title: "Ограничение по времени",
        step2Text: "У вас есть только 60 секунд, чтобы сделать выбор.",
        step3Title: "Колода",
        step3Text: "Нажмите ⭐, чтобы добавить работу в вашу колоду.",
        step3Btn: "Понял, спасибо",
        nextBtn: "Далее",
      },
      selectArtwork: "Выберите лучший арт",
      balance: "Баланс",
      voteBtn: {
        default: "Голосовать",
        withDeck: "Голосовать и добавить в колоду",
      },
    },
  },
  upload: {
    en: {
      sectionHeader: {
        titleSlot: (slot: string) => `${slot} Slot: Upload Art`,
      },
      aiState: {
        category: "Category",
        tags: "Tags",
        aiMentorReview: "AI Mentor Review",
        points: (current: number, max = 100) => `${current} / ${max}`,
      },
      formState: {
        titlePlaceholder: "Title *",
        descriptionPlaceholder: "Description",
        confirmRights: "I confirm that I have all copyrights for this art",
        agreeLegal: "I agree with Privacy Policy and Terms & Conditions.",
        nextBtn: "Next",
        saveBtn: "Save",
      },
    },
    ru: {
      sectionHeader: {
        titleSlot: (slot: string) => `Слот ${slot}: Загрузить арт`,
      },
      aiState: {
        category: "Категория",
        tags: "Теги",
        aiMentorReview: "Обзор AI-ментора",
        points: (current: number, max = 100) => `${current} / ${max}`,
      },
      formState: {
        titlePlaceholder: "Название *",
        descriptionPlaceholder: "Описание",
        confirmRights: "Подтверждаю, что обладаю всеми правами на этот арт",
        agreeLegal:
          "Согласен с Политикой конфиденциальности и Условиями использования.",
        nextBtn: "Далее",
        saveBtn: "Сохранить",
      },
    },
  },
  modal: {
    en: {
      cancel: "Cancel",
      confirm: "Confirm",
    },
    ru: {
      cancel: "Отмена",
      confirm: "Подтвердить",
    },
  },
  loading: {
    en: {
      defaultText: "Wait a second, please...",
    },
    ru: {
      defaultText: "Подождите секунду...",
    },
  },
  quizzes: {
    en: {
      label: "Quizzes",
      tabs: {
        announced: "Upcoming",
        active: "Active",
        archived: "Finished",
      },
      empty: {
        announced: "There isn't any upcoming quizzes",
        active: "There isn't any active quizzes",
        archived: "There isn't any finished quizzes",
      },
    },
    ru: {
      label: "Викторины",
      tabs: {
        announced: "Скоро",
        active: "Активные",
        archived: "Завершенные",
      },
      empty: {
        announced: "Нет будущих викторин",
        active: "Нет активных викторин",
        archived: "Нет завершенных викторин",
      },
    },
  },
  quizLeaderboard: {
    en: {
      label: "Quiz Leaderboard",
      loading: "Getting Quiz leaderboard. Please wait for a second...",
      by: "by",
      rater: "Rater",
      wins: "Wins",
    },
    ru: {
      label: "Таблица лидеров викторины",
      loading: "Получение таблицы лидеров. Пожалуйста, подождите...",
      by: "от",
      rater: "Оценщик",
      wins: "Победы",
    },
  },
  topic: {
    en: {
      by: "by",
      rater: "Rater",
      battles: "Battles",
      artworks: "Artworks",
    },
    ru: {
      by: "от",
      rater: "Rater",
      battles: "Баттлы",
      artworks: "Работы",
    },
  },
  voteResult: {
    en: {
      expertRate: "Expert Rate",
      reward: "Reward",
    },
    ru: {
      expertRate: "Рейтинг эксперта",
      reward: "Награда",
    },
  },
  notifications: {
    en: {
      sectionHeader: "Notifications",
      battlesTab: "Battles",
      votesTab: "Votes",
      noBattles: "You don't have finished battles",
      noVotes: "You don't have finished votes",
      clearAll: "Clear all notifications",
    },
    ru: {
      sectionHeader: "Уведомления",
      battlesTab: "Баттлы",
      votesTab: "Голоса",
      noBattles: "У вас нет завершённых баттлов",
      noVotes: "У вас нет завершённых голосований",
      clearAll: "Очистить все уведомления",
    },
  },
  battleWin: {
    en: {
      youWon: "You won",
      level: "Level",
      levelUp: "🚀 {from} → {to}",
      nft: "NFT",
      popupAlt: "View artwork",
    },
    ru: {
      youWon: "Вы выиграли",
      level: "Уровень",
      levelUp: "🚀 {from} → {to}",
      nft: "NFT",
      popupAlt: "Посмотреть работу",
    },
  },
  battleLose: {
    en: {
      youLose: "You lose",
      level: "Level",
    },
    ru: {
      youLose: "Вы проиграли",
      level: "Уровень",
    },
  },
  leaderboardHeader: {
    en: {
      leaderboard: "Leaderboard",
      yourPlace: "Your place",
    },
    ru: {
      leaderboard: "Таблица лидеров",
      yourPlace: "Ваше место",
    },
  },
  leaderboard: {
    en: {
      loadingLeaderboard: "Getting leaderboard. Please wait for a second...",
      artworks: "Artworks",
      authors: "Authors",
      voters: "Voters",
      winrate: "Winrate",
      expertRate: "Expert Rate",
    },
    ru: {
      loadingLeaderboard: "Получаем таблицу лидеров. Пожалуйста, подождите...",
      artworks: "Работы",
      authors: "Авторы",
      voters: "Голосующие",
      winrate: "Процент побед",
      expertRate: "Экспертный рейтинг",
    },
  },
  sectionHeader: {
    en: {
      skip: "Skip",
      replace: "Replace",
      cancel: "Cancel",
    },
    ru: {
      skip: "Пропустить",
      replace: "Заменить",
      cancel: "Отмена",
    },
  },
  bottomNav: {
    en: {
      gallery: "Gallery",
      quizzes: "Quizzes",
      vote: "Vote",
      leaderboard: "Ratings",
      wallet: "Deck",
    },
    ru: {
      gallery: "Галерея",
      quizzes: "Квизы",
      vote: "Голос",
      leaderboard: "Рейтинг",
      wallet: "Колода",
    },
  },
  nft: {
    en: {
      nft: "NFT",
      profit: "Profit",
      winRate: "WinRate",
      imageLevel: "Image Level",
      description: "Description",
      aiMentorReview: "AI Mentor Review",
      category: "Category",
      tags: "Tags",
      galleryBack: "Back to Gallery",
      zeroProfit: "0 🌕",
    },
    ru: {
      nft: "NFT",
      profit: "Прибыль",
      winRate: "Победы",
      imageLevel: "Уровень",
      description: "Описание",
      aiMentorReview: "Рецензия AI",
      category: "Категория",
      tags: "Тэги",
      galleryBack: "Назад",
      zeroProfit: "0 🌕",
    },
  },
  imagePopup: {
    en: {
      loading: "Loading...",
      by: "by",
      value: "Value",
      winRate: "WinRate",
      imageLevel: "Image Level",
    },
    ru: {
      loading: "Загрузка...",
      by: "автор",
      value: "Ценность",
      winRate: "Процент побед",
      imageLevel: "Уровень изображения",
    },
  },
  image: {
    en: {
      loading:
        "Reviewing your photo with AI. Please, be patient, it may take up to 30 seconds.",
      by: "by",
      value: "Value",
      profit: "Profit",
      winRate: "WinRate",
      imageLevel: "Image Level",
      description: "Description",
      aiMentorReview: "AI Mentor Review",
      expand: "⬇️ Expand",
      collapse: "⬆️ Collapse",
      tags: "Tags",
      battles: "Battles",
      pending: "Pending",
      ongoing: "Ongoing",
      finished: "Finished",
      won: "Won",
      emptyPending: "You don't have pending battles",
      emptyOngoing: "You don't have ongoing battles",
      emptyFinished: "You don't have finished battles",
      sendToBattle: "Send to Battle",
      lowPerformance:
        "😢 Your artwork’s performance is low. Consider replacing it with a new one to improve your results",
      highPerformance: "🔥 Your artwork is on fire this month!",
    },
    ru: {
      loading:
        "AI проверяет ваше фото. Пожалуйста, подождите, это может занять до 30 секунд.",
      by: "автор",
      value: "Ценность",
      profit: "Прибыль",
      winRate: "Процент побед",
      imageLevel: "Уровень изображения",
      description: "Описание",
      aiMentorReview: "Отзывы AI наставника",
      expand: "⬇️ Развернуть",
      collapse: "⬆️ Свернуть",
      tags: "Теги",
      battles: "Битвы",
      pending: "В ожидании",
      ongoing: "В процессе",
      finished: "Завершенные",
      won: "Выиграно",
      emptyPending: "У вас нет ожидающих битв",
      emptyOngoing: "У вас нет текущих битв",
      emptyFinished: "У вас нет завершенных битв",
      sendToBattle: "Отправить в битву",
      lowPerformance:
        "😢 Эффективность вашего произведения низкая. Подумайте о замене на новое, чтобы улучшить результаты",
      highPerformance: "🔥 Ваше произведение в топе этого месяца!",
    },
  },
  startBattle: {
    en: {
      sendToBattle: "Send to Battle",
      selectQuiz: "Select a Quiz",
      joinBattle: "Join Battle",
      level: "Level",
      lowARTC: "Not enough 🌕",
    },
    ru: {
      sendToBattle: "Отправить в битву",
      selectQuiz: "Выберите викторину",
      joinBattle: "Присоединиться к битве",
      level: "Уровень",
      lowARTC: "Недостаточно 🌕",
    },
  },
  gallerySlot: {
    en: {
      upload: "Upload",
      locked: "🔒 Locked",
      uploadArt: "Upload Art",
      sendToBattle: "Send to Battle",
      unlockPaid: "Unlock",
      value: "Value",
    },
    ru: {
      upload: "Загрузить",
      locked: "🔒 Закрыто",
      uploadArt: "Загрузить арт",
      sendToBattle: "Отправить в битву",
      unlockPaid: "Разблокировать",
      value: "Ценность",
    },
  },
  gallery: {
    en: {
      galleryHeader: "Gallery",
      balance: "Balance",
      galleryValue: "Gallery Value",
      yourGallerySlots: "Your Gallery Slots",
      getFree: "Get Free 🌕",
      left: "left",
      get10: "Get 10 🌕",
      inviteCode: "Invite code",
      invitePrompt: "Invite your Friends",
      invite5ToUnlock: "Invite 5 Friends and unlock a new slot in gallery.",
      invitedFriends:
        "You've already invited <b>{count}</b> friends! Thank you!",
      copy: "Copy",
      saved: "Saved",
      yourNFTs: "Your NFTs",
      nftHint:
        "Win the 7th level battle to transform the victorious artwork into NFT",
      reviewingPhoto:
        "Reviewing your photo with AI. Please, be patient, it may take up to 30 seconds.",
      gallerySlot: {
        upload: "Upload",
        locked: "🔒 Locked",
        uploadArt: "Upload Art",
        sendToBattle: "Send to Battle",
        unlockPaid: "Unlock",
        value: "Value",
      },
    },
    ru: {
      galleryHeader: "Галерея",
      balance: "Баланс",
      galleryValue: "Ценность галереи",
      yourGallerySlots: "Ваши слоты галереи",
      getFree: "Получить бесплатно 🌕",
      left: "осталось",
      get10: "Получить 10 🌕",
      inviteCode: "Код приглашения",
      invitePrompt: "Пригласите друзей",
      invite5ToUnlock: "Пригласите 5 друзей и откройте новый слот в галерее.",
      invitedFriends: "Вы уже пригласили <b>{count}</b> друзей! Спасибо!",
      copy: "Скопировать",
      saved: "Сохранено",
      yourNFTs: "Ваши NFT",
      nftHint: "Выиграйте битву 7 уровня, чтобы превратить победный арт в NFT",
      reviewingPhoto:
        "Проверка вашего фото с помощью ИИ. Пожалуйста, подождите, это может занять до 30 секунд.",
      gallerySlot: {
        upload: "Загрузить",
        locked: "🔒 Закрыто",
        uploadArt: "Загрузить арт",
        sendToBattle: "Отправить в битву",
        unlockPaid: "Разблокировать",
        value: "Ценность",
      },
    },
  },
} as const;
